/**
 * Trust levels a score may carry, and which of them count competitively.
 *
 * `SERVER_VERIFIED` means Riz derived the score itself — seed-plus-input-trace
 * replay, which is not built. Nothing writes it yet, and nothing should until
 * that exists.
 *
 * `CLIENT_VALIDATED` means the client reported the score and the server
 * validated it against the per-game policy: ceiling, server-measured duration,
 * velocity, and behavioural signals. That is what a completed run earns today.
 *
 * `MODERATED` means a run tripped the suspicion threshold and is awaiting an
 * operator decision. It is deliberately outside the competitive set, so a
 * flagged score is neither ranked nor lost.
 */
export const COMPETITIVE_SCORE_LEVELS = ['SERVER_VERIFIED', 'CLIENT_VALIDATED'] as const;

export type CompetitiveScoreLevel = (typeof COMPETITIVE_SCORE_LEVELS)[number];

export const COMPETITIVE_SCORE_LEVEL_SET: ReadonlySet<string> = new Set<string>(COMPETITIVE_SCORE_LEVELS);

/** The level a run earns by passing validation. */
export const VALIDATED_SCORE_LEVEL = 'CLIENT_VALIDATED';

/** The level a run earns by tripping the suspicion threshold. */
export const MODERATED_SCORE_LEVEL = 'MODERATED';

export function isCompetitiveScoreLevel(level: string): boolean {
  return COMPETITIVE_SCORE_LEVEL_SET.has(level);
}

export type ScoreTrustRow = {
  gameSlug: string;
  trustLevel: string;
  plays: number;
  score: number;
  rpEarned: number;
};

export type ScoreTotals = {
  plays: number;
  totalScore: number;
  rpAwarded: number;
  trustedPlays: number;
};

export function emptyScoreTotals(): ScoreTotals {
  return { plays: 0, totalScore: 0, rpAwarded: 0, trustedPlays: 0 };
}

/**
 * Folds per-trust-level score groups into one row per game, keeping the
 * competitive subset countable so an operator surface never reports a mixed
 * population as a single number.
 */
export function scoreTotalsByGame(rows: readonly ScoreTrustRow[]): Map<string, ScoreTotals> {
  const totals = new Map<string, ScoreTotals>();
  for (const row of rows) {
    const current = totals.get(row.gameSlug) ?? emptyScoreTotals();
    current.plays += row.plays;
    current.totalScore += row.score;
    current.rpAwarded += row.rpEarned;
    if (isCompetitiveScoreLevel(row.trustLevel)) current.trustedPlays += row.plays;
    totals.set(row.gameSlug, current);
  }
  return totals;
}
